import { useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useController, useFormContext } from "react-hook-form";
import { useTranslations } from '@fenetech/translations';
import useTerms from 'helpers/context/SelectionValues/useTerms'

import { IQuotePermissions } from "helpers/interfaces";
import useQuoteData from "../useQuoteData";
import { QuoteRequiredFieldEnum } from "helpers/enums";

interface ISelectTerms {
    parentCustomerID: string,
    permissions?: IQuotePermissions,
    [x: string] : any  // this should always be the last prop, to support any other props that will be passed along down the component chain
}

function SelectTerms ({ parentCustomerID, permissions, ...rest }: ISelectTerms)
{

    const tm = useTranslations();
    const { control, formState: { errors } } = useFormContext();

    const termsList = useTerms(parentCustomerID);
    const { requiredFields } = useQuoteData();
    
    const isRequired = useMemo(() => {
        if (requiredFields) {
            if (requiredFields.includes(QuoteRequiredFieldEnum.Terms)) {
                return true;
            }
        }
        return false;
    }, [requiredFields]);

    const isRequiredTranslation = useMemo(() => {
        return tm.GetWithParams("{0} cannot be blank.", tm.Get("Terms"));
    }, [tm]);

    const { field } = useController({
        name: "billing.terms", 
        control,
        rules: {
            required: { value: isRequired, message: isRequiredTranslation }
        },
      });

    const values = useMemo(() => {
        return termsList?.map(({description}) => description) ?? [];
    }, [termsList]);

    return (
        <Autocomplete 
            id="terms"
            autoComplete
            autoSelect
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            options={values} 
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
                <TextField 
                    variant="outlined" 
                    error={(errors?.billing?.terms?.message) ? true : false}
                    helperText={errors?.billing?.terms?.message}                     
                    {...params} 
                    label={`${isRequired ? "*" : ""}${tm.Get("Terms")}`} 
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                            {(termsList === undefined) && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                            </>
                        ),
                    }}                    
                    />
                )}
            onChange={(_, data) => {field.onChange(data)}}
            value={(field.value === "") ? null : field.value}
            ref={field.ref}
            disabled={permissions ? !permissions.terms : true}
            {...rest}
        /> 
    );

}

export default SelectTerms;